import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, TextField, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import toast from 'react-hot-toast';
import { api } from '../../api/api';

const STATUS_COLOR = { paid: '#22c55e', pending: '#f59e0b', overdue: '#ef4444', cancelled: '#64748b' };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
}

function actionLabel(a) {
  const m = { create: '생성', update: '수정', delete: '삭제', align: '정산(납부일 기준)' };
  return m[a] || a;
}

export default function StorePaymentDialog({ open, onClose, store, onApplied, onStorePatch }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [alignDate, setAlignDate] = useState('');
  const [alignMemo, setAlignMemo] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [editMemo, setEditMemo] = useState('');
  const [editForm, setEditForm] = useState({ status: 'pending', amount: '', dueDate: '', paidDate: '', memo: '' });

  const load = async () => {
    if (!store?._id) return;
    setLoading(true);
    try {
      const [pr, lr] = await Promise.all([
        api.get(`/payment/bystore/${store._id}`),
        api.get(`/payment/audit/store/${store._id}?limit=200`),
      ]);
      setPayments(pr.data.payments || []);
      setLogs(lr.data.logs || []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && store?._id) {
      load();
      const d = store.paymentLastAlignedDate
        ? new Date(store.paymentLastAlignedDate).toISOString().slice(0, 10)
        : '';
      setAlignDate(d);
      setAlignMemo('');
      setTab(0);
    }
  }, [open, store?._id]);

  const handleAlign = async () => {
    if (!alignDate) {
      toast.error('마지막 납부 기준일을 선택해 주세요.');
      return;
    }
    if ((alignMemo || '').trim().length < 3) {
      toast.error('정산 사유(메모)를 3자 이상 입력해 주세요.');
      return;
    }
    try {
      const { data } = await api.post(`/payment/align-store/${store._id}`, {
        lastPaidThroughDate: alignDate,
        editMemo: alignMemo.trim(),
      });
      toast.success(`정산 반영: ${data.updatedCount || 0}건 변경`);
      setAlignMemo('');
      await load();
      if (data.store) onStorePatch?.(data.store);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const openEdit = (p) => {
    setEditRow(p);
    setEditMemo('');
    setEditForm({
      status: p.status || 'pending',
      amount: String(p.amount ?? ''),
      dueDate: p.dueDate ? String(p.dueDate).slice(0, 10) : '',
      paidDate: p.paidDate ? String(p.paidDate).slice(0, 10) : '',
      memo: p.memo || '',
    });
  };

  const saveEdit = async () => {
    if ((editMemo || '').trim().length < 3) {
      toast.error('수정 사유(메모)를 3자 이상 입력해 주세요.');
      return;
    }
    try {
      await api.patch(`/payment/update/${editRow._id}`, {
        editMemo: editMemo.trim(),
        status: editForm.status,
        amount: Number(editForm.amount),
        dueDate: editForm.dueDate || null,
        paidDate: editForm.paidDate || null,
        memo: editForm.memo,
      });
      toast.success('저장되었습니다.');
      setEditRow(null);
      await load();
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  if (!store) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
        <DialogTitle sx={{
          fontWeight: 700, color: '#551FFF', borderBottom: '3px solid #551FFF',
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <AttachMoneyIcon />
          결제 · 청구 — {store.tljNo}
          <Chip size="small" label={store.paymentOption === 'yearly' ? '연간' : '월간'} sx={{ ml: 1 }} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            결제 방식(월간/연간)은 점포 수정 화면에서 설정합니다. 아래는 해당 점포의 청구·납부·감사 이력입니다.
          </Typography>

          {store.paymentLastAlignedDate && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f8f7ff', borderColor: '#d4c9ff' }}>
              <Typography variant="caption" color="text.secondary">마지막 정산 적용일(서버 기록)</Typography>
              <Typography variant="body2" fontWeight={600}>
                {fmtDate(store.paymentLastAlignedDate)}
                {store.paymentLastAlignedMemo ? ` — ${store.paymentLastAlignedMemo}` : ''}
              </Typography>
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-end' }}>
            <TextField
              size="small" type="date" label="마지막 납부 기준일"
              value={alignDate} onChange={(e) => setAlignDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="이 날짜까지의 청구월·기간은 납부완료로 맞추고, 이후는 연체/미납으로 정리합니다."
            />
            <TextField
              size="small" label="정산 사유(감사 메모, 필수)" value={alignMemo}
              onChange={(e) => setAlignMemo(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
              placeholder="예: 2월 수취 확인, 전화 납부 반영"
            />
            <Button variant="contained" onClick={handleAlign} sx={{ background: '#551FFF', mb: 0.5 }}>
              정산 적용
            </Button>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <Tab label="청구 / 납부 내역" />
            <Tab label="변경 감사 로그" />
          </Tabs>

          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={32} /></Box>
          ) : tab === 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f3ff' }}>
                    <TableCell>청구월</TableCell>
                    <TableCell>금액</TableCell>
                    <TableCell>상태</TableCell>
                    <TableCell>만기</TableCell>
                    <TableCell>납부일</TableCell>
                    <TableCell width={80}>수정</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(payments || []).length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#999' }}>청구 내역이 없습니다.</TableCell></TableRow>
                  ) : (
                    payments.map((p) => (
                      <TableRow key={p._id} hover>
                        <TableCell>{p.billingMonth || '—'}</TableCell>
                        <TableCell>${Number(p.amount || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip size="small" label={p.status} sx={{ color: STATUS_COLOR[p.status] || '#333', fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>{fmtDate(p.dueDate)}</TableCell>
                        <TableCell>{fmtDate(p.paidDate)}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => openEdit(p)} sx={{ textTransform: 'none', fontSize: 11 }}>수정</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f3ff' }}>
                    <TableCell>일시</TableCell>
                    <TableCell>작업</TableCell>
                    <TableCell>담당자</TableCell>
                    <TableCell>메모 / 요약</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(logs || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: '#999' }}>감사 로그가 없습니다.</TableCell></TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log._id} hover>
                        <TableCell>{new Date(log.createdAt).toLocaleString('ko-KR')}</TableCell>
                        <TableCell>{actionLabel(log.action)}</TableCell>
                        <TableCell>{log.actorLabel || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>
                          {log.editMemo || '—'}
                          {log.action === 'align' && log.snapshotAfter?.changeCount != null && (
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              변경 건수: {log.snapshotAfter.changeCount}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { load(); toast.success('새로고침'); }}>새로고침</Button>
          <Button onClick={onClose} sx={{ color: '#888' }}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>청구 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            <TextField select SelectProps={{ native: true }} size="small" label="상태"
              value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}>
              <option value="pending">pending</option>
              <option value="paid">paid</option>
              <option value="overdue">overdue</option>
              <option value="cancelled">cancelled</option>
            </TextField>
            <TextField size="small" label="금액" type="number" value={editForm.amount}
              onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))} />
            <TextField size="small" label="만기일" type="date" value={editForm.dueDate} InputLabelProps={{ shrink: true }}
              onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))} />
            <TextField size="small" label="납부일" type="date" value={editForm.paidDate} InputLabelProps={{ shrink: true }}
              onChange={(e) => setEditForm((f) => ({ ...f, paidDate: e.target.value }))} />
            <TextField size="small" label="청구 메모" value={editForm.memo} multiline rows={2}
              onChange={(e) => setEditForm((f) => ({ ...f, memo: e.target.value }))} />
            <TextField size="small" label="수정 사유 (필수, 감사용)" value={editMemo} multiline rows={2} required
              placeholder="다른 담당자가 알 수 있게 이유를 적어 주세요."
              onChange={(e) => setEditMemo(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>취소</Button>
          <Button variant="contained" onClick={saveEdit} sx={{ background: '#551FFF' }}>저장</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
